import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import { z } from 'zod';

export type { CountryCode };

/** Phone numbers are stored in E.164 (`+12015550123`). Optional, never verified, no SMS. */
const E164 = /^\+[1-9]\d{6,14}$/;

export const PHONE_INVALID = 'Enter a valid phone number, including the country (e.g. +1 201 555 0123).';

/** What someone typed, read for the chosen country ("(201) 555-0123" + US) → E.164, or null when it isn't a valid number. */
export function normalizePhone(input: string, country: CountryCode = 'US'): string | null {
  const parsed = parsePhoneNumberFromString(input.trim(), country);
  return parsed?.isValid() ? parsed.number : null;
}

export function isValidE164(value: string): boolean {
  return E164.test(value) && !!parsePhoneNumberFromString(value)?.isValid();
}

/** "+1 201 555 0123" for display. */
export function formatPhone(e164: string): string {
  return parsePhoneNumberFromString(e164)?.formatInternational() ?? e164;
}

/** The country an E.164 number belongs to (for the country selector), e.g. 'US'. */
export function phoneCountry(e164: string): CountryCode | undefined {
  return parsePhoneNumberFromString(e164)?.country;
}

/** Every country with its calling code, for the selector. */
export const PHONE_COUNTRIES: { code: CountryCode; dial: string }[] = getCountries().map((code) => ({ code, dial: `+${getCountryCallingCode(code)}` }));

/** A phone number in a request body: E.164 and valid (the client normalizes; the server checks again). */
export const PhoneNumber = z.string().refine(isValidE164, PHONE_INVALID);
